<template>
  <v-form ref="form"
          method="post"
          enctype="multipart/form-data"
          @submit.prevent="onSubmit">
    <v-card-text>
      <v-autocomplete label="Prediction"
                      :items="predictions"
                      item-text="name"
                      item-value="uuid"
                      v-model="uuid"
                      required
                      clearable />
      <v-row>
        <v-col>
          <v-autocomplete label="Targets"
                          :items="target_options"
                          v-model="config.targets"
                          :rules="rules.minimum"
                          multiple
                          required
                          clearable />
        </v-col>
        <v-col>
          <v-autocomplete label="Methods"
                          :items="method_options"
                          v-model="config.methods"
                          :rules="rules.minimum"
                          multiple
                          required
                          clearable />
        </v-col>
        <v-col>
          <v-autocomplete v-model="config.technique"
                          :items="techniques"
                          item-text="name"
                          item-value="slug"
                          label="Technique"
                          clearable></v-autocomplete>
        </v-col>
      </v-row>
      <v-row class="align-center">
        <v-col cols="auto">
          <v-checkbox label="Include history"
                      v-model="config.history"
                      :disabled="disableHistory"></v-checkbox>
        </v-col>
        <v-col cols="auto">
          <v-checkbox label="Include predictions"
                      v-model="config.predictions"></v-checkbox>
        </v-col>
        <v-col cols="auto">
          <v-checkbox label="Include forecast"
                      v-model="config.forecast"></v-checkbox>
        </v-col>
        <v-col>
          <v-text-field v-model.number="config.months"
                        label="Forecast duration"
                        suffix="months"
                        class="mt-0 pt-0"
                        type="number"
                        hide-spin-buttons
                        hide-details
                        required
                        :disabled="!config.forecast" />
        </v-col>
      </v-row>
      <v-row class="align-center">
        <v-col cols="auto">
          <v-checkbox label="Terminate forecasts" :disabled="!config.forecast"
                      v-model="config.terminate"></v-checkbox>
        </v-col>
        <v-col cols="auto">
          <v-radio-group v-model="config.strategy"
                         row
                         :disabled="!config.terminate">
            <v-radio v-for="(option, o) in strategies"
                     :key="o"
                     :label="option.text"
                     :value="option.value"
                     required
                     :disabled="!config.forecast || option.disabled" />
          </v-radio-group>
        </v-col>
        <v-col>
          <v-text-field v-model.number="config.limit"
                        :suffix="strategy_suffix"
                        class="mt-0 pt-0"
                        type="number"
                        :hint="strategy_hint"
                        hide-spin-buttons
                        persistent-hint
                        required
                        :disabled="!config.terminate" />
        </v-col>
      </v-row>
      <v-autocomplete multiple
                      chips
                      deletable-chips
                      v-model="config.columns"
                      :items="column_options"
                      label="Select data columns to export"
                      clearable>
        <template v-slot:prepend-item>
          <v-list-item @mousedown.prevent @click="toggle">
            <v-list-item-action>
              <v-icon :color="config.columns.length > 0 ? 'indigo darken-4' : ''">
                {{ icon }}
              </v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                Select All
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider class="mt-2"></v-divider>
        </template>
      </v-autocomplete>
      <v-card flat
              outlined>
        <v-card-title>Export to database...</v-card-title>
        <v-card-subtitle>You can optionally choose to export this prediction directly into a database
          table</v-card-subtitle>
        <v-card-text>
          <v-autocomplete label="Select a database connection"
                          :items="connections"
                          item-text="server"
                          item-value="id"
                          v-model="config.connection"
                          clearable />
          <v-autocomplete label="Select a table for export"
                          :items="tables"
                          v-model="config.table"
                          clearable />
        </v-card-text>
      </v-card>
    </v-card-text>
    <v-card-actions>
      <v-btn depressed
             class="success"
             type="submit"
             :disabled="!isValid">Generate</v-btn>
      <v-btn text
             type="button"
             @click="$router.go(-1)">Cancel</v-btn>
    </v-card-actions>
  </v-form>
</template>
<script>
import Predictions from "@/api/predictions"
import Connections from "@/api/connections"
export default {
  name: "ExportForm",
  props: {
    predictions: { type: Array, required: true },
    connections: { type: Array, required: true },
    techniques: { type: Array, required: true },
    instance: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      uuid: null,
      prediction: null,
      strategies: [
        { text: 'Duration', value: 'duration', hint: 'Cut off forecasts after a certain number of months', unit: 'months', disabled: false },
        { text: 'Production', value: 'production', hint: 'Cut off forecasts when monthly volume falls below a certain threshold', unit: 'bbl/m', disabled: false },
        { text: 'Decline Rate', value: 'decline', hint: 'Cut off forecasts when annual volume declines at a certain limit', unit: '% annual decline limit', disabled: false },
      ],
      tables: [],
      config: {
        targets: [],
        methods: [],
        technique: null,
        history: false,
        predictions: false,
        forecast: false,
        months: null,
        terminate: false,
        strategy: null,
        limit: null,
        columns: [],
        connection: null,
        table: null,
      },
      rules: {
        required: [v => !!v || "Field is required"],
        minimum: [v => v.length > 0 || "Minimum one is required"],
      },
    }
  },
  mounted() {
    if (!this.isEmpty(this.instance)) {
      this.uuid = this.instance.prediction.uuid
      this.config = this.instance.config
    }
  },
  watch: {
    uuid: function (val) {
      if (val) {
        Predictions.retrieve(val, `{id,uuid,name}`).then(response => {
          this.prediction = response.data
        })
      } else {
        this.config.targets = []
        this.config.methods = []
        this.config.technique = null
        this.config.history = false
        this.config.predictions = false
        this.config.forecast = false
        this.config.months = null
        this.config.terminate = false
        this.config.strategy = null
        this.config.limit = null
        this.config.columns = []
        this.config.connection = null
        this.config.table = null
      }
    },
    "config.connection": function (val) {
      if (val) {
        Connections.tables(this.config.connection).then(response => this.tables = response.data)
      }
    },
    "config.terminate": function (val) {
      if (!val) {
        this.config.strategy = null
        this.config.limit = null
      }
    },
    "config.forecast": function (val) {
      if (!val) {
        this.config.terminate = false
        this.config.months = null
        this.config.strategy = null
        this.config.limit = null
      } else {
        this.config.months = this.prediction.config.months
      }
    },
  },
  computed: {
    likesAllFruit () {
      return this.config.columns.length === this.column_options.length
    },
    likesSomeFruit () {
      return this.config.columns.length > 0 && !this.likesAllFruit
    },
    icon () {
      if (this.likesAllFruit) return 'mdi-close-box'
      if (this.likesSomeFruit) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    disableHistory() {
      if (!this.prediction) return false
      return this.prediction.application.slug == "predict"
    },
    target_options() {
      if (!this.prediction) return []
      let targets = []
      for (let [key, value] of Object.entries(this.prediction.config.targets)) {
        if (value != null) targets.push({ text: this.capitalize(key), value: key })
      }
      return targets
    },
    method_options() {
      if (!this.prediction) return []
      let methods = []
      this.prediction.methods.forEach(element => {
        methods.push({ text: element.name, value: element.slug })
      })
      return methods
    },
    strategy_suffix() {
      if (!this.config.strategy) return ''
      let strategy = this.strategies.find(s => s.value == this.config.strategy)
      if (strategy && strategy.unit) return strategy.unit
      return ''
    },
    strategy_hint() {
      if (!this.config.strategy) return ''
      let strategy = this.strategies.find(s => s.value == this.config.strategy)
      if (strategy && strategy.hint) return strategy.hint
      return ''
    },
    isValid() {
      let targets = !this.isEmpty(this.config.targets)
      let methods = !this.isEmpty(this.config.methods)
      let technique = !!this.config.technique
      let history = !!this.config.history
      let predictions = !!this.config.predictions
      let forecast = !!this.config.forecast
      let months = !!this.config.months
      let terminate = !!this.config.terminate
      let strategy = !!this.config.strategy
      let limit = !!this.config.limit

      if (!(targets && methods && technique)) return false
      if (!(history || predictions || forecast)) return false
      if (forecast && !months) return false
      if (terminate && !strategy) return false
      if (terminate && strategy && !limit) return false

      return true
    },
    column_options() {
      if (!this.prediction) return []
      return this.prediction.config.filters
    }
  },
  methods: {
    toggle () {
      this.$nextTick(() => {
        if (this.likesAllFruit) {
          this.config.columns = []
        } else {
          this.config.columns = this.column_options.slice()
        }
      })
    },
    onSubmit() {
      if (this.$refs.form.validate()) {
        let data = {
          prediction: this.prediction.id,
          config: this.config
        }
        // Add direction for termination if enabled (exports use reverse direction for aggregates)
        if (this.config.terminate) {
          data.config.direction = "reverse"
        }
        this.$emit("submitted", data)
      }
    },
    isEmpty(obj) {
      return Object.keys(obj).length === 0
    },
    capitalize(string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    },
  }
}
</script>